import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Header } from '@app/shared/components/layout/header/header';
import { Footer } from '@app/shared/components/layout/footer/footer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { AppStore } from '@store/app.store';
import { selectIsAuthenticated, selectIsLoading } from '@store/store-auth-user/auth-user.selectors';
import { Title } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthUserActions } from '@store/store-auth-user/auth-user.actions';
import { TopBanner } from '@app/shared/components/layout/top-banner/top-banner';
import { ThemeService } from 'src/styles/service/theme.service';

export type SupportedLanguage = 'sv' | 'en' | 'da' | 'fi' | 'no';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterOutlet, Header, Footer, ProgressSpinnerModule, ToastModule, ConfirmDialogModule, TopBanner],
    providers: [ConfirmationService],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
    private store = inject(Store<AppStore>);
    private titleService = inject(Title);
    private translate = inject(TranslateService);
    private themeService = inject(ThemeService);
    public readonly isLoading = toSignal(this.store.select(selectIsLoading));
    public readonly isAuthenticated = toSignal(this.store.select(selectIsAuthenticated));
    public readonly environmentName = environment.environmentName;

    public ngOnInit(): void {
        this.initStore();
    }

    public setTitleIfNotProd(): void {
        if (environment.environmentName !== 'Production') {
            this.titleService.setTitle(`Yllw Webshop - ${environment.environmentName}`);
        }
    }

    private initStore(): void {
        this.setTitleIfNotProd();
        this.initializeLanguage();
        this.store.dispatch(AuthUserActions.validatePublicAuthentication());
    }

    private initializeLanguage(): void {
        const availableLanguages: SupportedLanguage[] = ['sv', 'en', 'da', 'fi', 'no'];
        let savedLanguage: SupportedLanguage | null = null;

        try {
            savedLanguage = localStorage.getItem('app-language') as SupportedLanguage;
        } catch (error) {
            console.warn('[AppComponent] Could not access localStorage:', error);
        }

        const browserLanguage = this.translate.getBrowserLang() as SupportedLanguage;
        const languageToUse = savedLanguage || (availableLanguages.includes(browserLanguage) ? browserLanguage : 'sv');

        this.translate.setDefaultLang('sv');
        this.translate.use(languageToUse).subscribe({
            next: () => {
                document.documentElement.lang = languageToUse;
                try {
                    localStorage.setItem('app-language', languageToUse);
                } catch (error) {
                    console.warn('[AppComponent] Could not save language to localStorage:', error);
                }
            },
            error: error => {
                console.error('[AppComponent] Failed to load translations:', error);
            },
        });
    }
}
